// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// WHERE IT WAS SOLD — a channel composition band, and deliberately NOT a map.
//
// `field_psa_recall_location_sold` is a list field that mixes three different
// kinds of thing in one place: a national flag ("Nationally"), a sales CHANNEL
// ("Online"), and occasionally a state. It is not a clean state array, and the
// overwhelming majority of recalls are national.
//
// Drawing a choropleth off it would produce a map whose colour was mostly an
// artefact of which recalls happened to name a state, over a base of "everyone,
// everywhere". That is a fabricated map, so this view does not draw one. The
// honest question the field CAN answer is whether online-sold merchandise is a
// growing share of what gets recalled, and that is a composition band.
//
// The buckets are mutually exclusive by construction — a recall can be both
// national and online, so a priority order is applied and stated on the page —
// otherwise the segments of a "100%" chart would add to more than 100%.

import { getBundle } from '../data';
import { num } from '../format';
import { chartFrame, labelChart, linear, markTip, svgEl } from '../components/svg';

const BANDS = [
  { key: 'online', label: 'Sold online (including marketplaces)', colour: '#c2410c' },
  { key: 'nationalOnly', label: 'Sold nationally, in stores', colour: '#0f5c63' },
  { key: 'stateOnly', label: 'Named states only', colour: '#6d4aa8' },
  { key: 'unknown', label: 'Not recorded', colour: '#b3a992' },
] as const;

const W = 900;
const H = 400;
const M = { top: 16, right: 16, bottom: 40, left: 46 };

export function renderChannels(host: HTMLElement): void {
  const { stats, meta } = getBundle();
  const rows = stats.channels;
  const latest = rows.filter((r) => r.year < meta.accc.partialYear).slice(-1)[0];
  const early = rows.find((r) => r.year === 2010) ?? rows[0];
  const shareOf = (r: typeof rows[number]) => (r.total ? (100 * r.online) / r.total : 0);

  host.innerHTML = `
    <div class="view-head">
      <h2>Where recalled products were sold</h2>
      <p>Recalls that name an online sales channel went from ${shareOf(early).toFixed(1)}% of the year's recalls
      in ${early.year} to ${shareOf(latest).toFixed(1)}% in ${latest.year}. Everything sold through a
      marketplace still has an Australian supplier responsible for it — that is what makes these recalls
      possible at all.</p>
    </div>
    <div id="band"></div>
    <div class="legend">
      ${BANDS.map((b) => `<span class="legend-item"><span class="legend-swatch" style="background:${b.colour}"></span>${b.label}</span>`).join('')}
    </div>
    <div class="note caveat">
      <strong>Why there is no map here.</strong> The register records where a product was sold as a single list
      field mixing “Nationally”, a sales channel, and occasionally a state. Most recalls are national, so a
      map coloured from this field would mostly redraw where Australians live. The categories above are made
      mutually exclusive in this order — online first, then national, then state-only, then not recorded — so
      the bands sum to exactly 100% of each year. A recall that is both national and online is counted as
      online. The final year on this chart is a <strong>partial</strong> year: it is a share of the recalls
      published so far, and is not comparable with the complete years beside it.
    </div>
  `;

  host.querySelector<HTMLElement>('#band')!.append(band(rows, meta.accc.partialYear));
}

function band(
  rows: ReturnType<typeof getBundle>['stats']['channels'],
  partial: number,
): HTMLElement {
  const { figure, mount } = chartFrame({
    title: 'Sales channel as a share of each year’s recalls',
    subtitle: 'Bands fill each year completely, so this is composition — the count of recalls per year is on the “What changed” view.',
    scroll: true,
    minWidth: 620,
  });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMidYMid meet' });
  svg.style.minWidth = '620px';
  labelChart(svg, 'Sales channel share of recalls by year',
    'A 100% stacked area chart. The online band grows from almost nothing to a substantial share after 2015.');

  const x = linear([rows[0].year, rows[rows.length - 1].year], [M.left, W - M.right]);
  const y = linear([0, 100], [H - M.bottom, M.top]);
  const base = new Map<number, number>(rows.map((r) => [r.year, 0]));

  for (const b of BANDS) {
    const top: string[] = [];
    const bottom: string[] = [];
    for (const r of rows) {
      const share = r.total ? (100 * (r[b.key] as number)) / r.total : 0;
      const start = base.get(r.year)!;
      top.push(`${x(r.year).toFixed(2)},${y(start + share).toFixed(2)}`);
      bottom.unshift(`${x(r.year).toFixed(2)},${y(start).toFixed(2)}`);
      base.set(r.year, start + share);
    }
    const path = svgEl('path', {
      d: `M${top.join(' L')} L${bottom.join(' L')} Z`,
      fill: b.colour, 'fill-opacity': 0.92, class: 'mark',
    });
    markTip(path, b.label, b.label);
    svg.append(path);
  }

  const step = (W - M.left - M.right) / Math.max(1, rows.length - 1);
  for (const r of rows) {
    const rect = svgEl('rect', {
      x: (x(r.year) - step / 2).toFixed(2), y: M.top,
      width: step.toFixed(2), height: H - M.top - M.bottom, fill: 'transparent', class: 'mark',
    });
    markTip(rect,
      `${r.year}${r.year === partial ? ' (partial year)' : ''}\n${num(r.total)} recalls\n`
      + BANDS.map((b) => `${b.label}: ${((100 * (r[b.key] as number)) / (r.total || 1)).toFixed(0)}%`).join('\n'),
      `${r.year}: ${num(r.total)} recalls`);
    svg.append(rect);
  }

  for (const t of [0, 25, 50, 75, 100]) {
    svg.append(svgEl('line', {
      x1: M.left, x2: W - M.right, y1: y(t).toFixed(2), y2: y(t).toFixed(2),
      stroke: 'rgba(255,255,255,.35)', 'stroke-width': 1, 'pointer-events': 'none',
    }));
    const lab = svgEl('text', { x: M.left - 6, y: (y(t) + 4).toFixed(2), class: 'axis-text', 'text-anchor': 'end' });
    lab.textContent = `${t}%`;
    svg.append(lab);
  }

  const span = rows[rows.length - 1].year - rows[0].year;
  const stepYears = span > 20 ? 5 : 2;
  for (const r of rows) {
    if (r.year % stepYears !== 0) continue;
    const t = svgEl('text', { x: x(r.year).toFixed(2), y: H - M.bottom + 18, class: 'axis-text', 'text-anchor': 'middle' });
    t.textContent = String(r.year);
    svg.append(t);
  }

  mount.append(svg);
  return figure;
}
